import React, { useCallback, useEffect, useState } from "react";
import { FaCog, FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import {
  apihubDelete,
  apihubPost,
} from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const AUTH_TYPES = ["none", "basic", "bearer"];
const PAYLOAD_TYPES = ["parameters", "rawjson"];
const PARAM_POSITIONS = ["query", "header", "body"];

const emptyForm = () => ({
  providername: "",
  baseurl: "",
  method: "POST",
  authorizationtype: "none",
  basicauthuserinfo: "",
  basicauthpasswordinfo: "",
  bearertokeninfo: "",
  payloadtype: "parameters",
  parameter: [{ key: "", value: "", position: "query" }],
  rawjson: "",
  mapping: "",
  successresponse: "",
  errorresponse: "",
});

const parseJsonField = (label, value, required) => {
  if (!value || !String(value).trim()) {
    return required ? undefined : undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    throw new Error(`Invalid JSON in ${label}`);
  }
};

const buildPayload = (projectId, form, editId) => {
  const body = {
    projectid: projectId,
    providername: form.providername,
    baseurl: form.baseurl,
    method: form.method,
    authorizationtype: form.authorizationtype,
    basicauthuserinfo:
      form.authorizationtype === "basic" ? form.basicauthuserinfo : "",
    basicauthpasswordinfo:
      form.authorizationtype === "basic" ? form.basicauthpasswordinfo : "",
    bearertokeninfo:
      form.authorizationtype === "bearer" ? form.bearertokeninfo : "",
    payloadtype: form.payloadtype,
    parameter:
      form.payloadtype === "parameters"
        ? (form.parameter || []).filter((p) => p && (p.key || p.value))
        : [],
    rawjson:
      form.payloadtype === "rawjson"
        ? parseJsonField("Raw JSON", form.rawjson, false)
        : undefined,
    mapping: parseJsonField("Mapping", form.mapping, false) || {},
    successresponse: parseJsonField("Success Response", form.successresponse, true),
    errorresponse: parseJsonField("Error Response", form.errorresponse, true),
  };
  if (editId) body.activeeditid = editId;
  return body;
};

const SmsConfig = () => {
  const { projectId } = useProjectPhone();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubPost(
        `/getInteractedCustomerDetail/fetchallsmsconfig?page=${page}&limit=${limit}`,
        { projectId },
      );
      if (res?.success) {
        setRows(res.data || []);
        setTotalPages(res.pagination?.totalPages || 1);
      } else {
        setRows([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch SMS configs");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setModalOpen(true);
    setMessage("");
    setError("");
  };

  const openEdit = (row) => {
    setEditId(row._id);
    setForm({
      providername: row.providername || "",
      baseurl: row.baseurl || "",
      method: row.method || "POST",
      authorizationtype: row.authorizationtype || "none",
      basicauthuserinfo: row.basicauthuserinfo || "",
      basicauthpasswordinfo: row.basicauthpasswordinfo || "",
      bearertokeninfo: row.bearertokeninfo || "",
      payloadtype: row.payloadtype || "parameters",
      parameter: Array.isArray(row.parameter)
        ? row.parameter.map((p) => ({
            key: p?.key || "",
            value: p?.value || "",
            position: p?.position || "query",
          }))
        : [{ key: "", value: "", position: "query" }],
      rawjson: row.rawjson ? JSON.stringify(row.rawjson, null, 2) : "",
      mapping: row.mapping ? JSON.stringify(row.mapping, null, 2) : "",
      successresponse: row.successresponse
        ? JSON.stringify(row.successresponse, null, 2)
        : "",
      errorresponse: row.errorresponse
        ? JSON.stringify(row.errorresponse, null, 2)
        : "",
    });
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onParamChange = (index, field, value) => {
    setForm((prev) => {
      const parameter = [...prev.parameter];
      parameter[index] = { ...parameter[index], [field]: value };
      return { ...prev, parameter };
    });
  };

  const addParam = () => {
    setForm((prev) => ({
      ...prev,
      parameter: [...prev.parameter, { key: "", value: "", position: "query" }],
    }));
  };

  const removeParam = (index) => {
    setForm((prev) => ({
      ...prev,
      parameter: prev.parameter.filter((_, i) => i !== index),
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.providername || !form.baseurl || !form.method) {
      setError("Please fill required fields (provider, base URL, method).");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = buildPayload(projectId, form, editId);
      const path = editId
        ? "/getInteractedCustomerDetail/editsmsconfig"
        : "/getInteractedCustomerDetail/smsconfigcreate";
      const res = await apihubPost(path, payload);
      if (res?.success === false) {
        throw new Error(res.message || "Save failed");
      }
      setMessage(res?.message || (editId ? "Config updated" : "Config created"));
      setModalOpen(false);
      setForm(emptyForm());
      setEditId(null);
      load();
    } catch (err) {
      setError(err.message || "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this SMS configuration?")) return;
    setError("");
    try {
      const res = await apihubDelete(
        `/getInteractedCustomerDetail/deletesmsconfig/${id}`,
      );
      if (res?.success === false) {
        throw new Error(res.message || "Delete failed");
      }
      setMessage(res?.message || "Deleted");
      load();
    } catch (err) {
      setError(err.message || "Failed to delete config");
    }
  };

  return (
    <div className="biz-panel-page biz-smsconfig-page">
      <div className="biz-panel-header biz-panel-header-row">
        <h1>
          <FaCog />
          SMS Config
        </h1>
        <button type="button" className="biz-btn-primary" onClick={openCreate}>
          <FaPlus /> Add Config
        </button>
      </div>

      {message && <p className="biz-banner biz-banner-success">{message}</p>}
      {error && !modalOpen && (
        <p className="biz-banner biz-banner-error">{error}</p>
      )}

      <div className="biz-table-wrap">
        {loading ? (
          <p className="biz-table-message">Loading SMS configurations…</p>
        ) : (
          <table className="biz-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Provider</th>
                <th>Base URL</th>
                <th>Method</th>
                <th>Auth</th>
                <th className="biz-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="biz-table-empty">
                    No SMS configurations yet.
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row._id}>
                    <td>{(page - 1) * limit + idx + 1}</td>
                    <td>{row.providername}</td>
                    <td className="biz-table-mono">{row.baseurl}</td>
                    <td>{row.method}</td>
                    <td>{row.authorizationtype || "none"}</td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions">
                        <button
                          type="button"
                          className="biz-btn-icon"
                          title="Edit"
                          onClick={() => openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="biz-btn-icon biz-btn-icon-danger"
                          title="Delete"
                          onClick={() => onDelete(row._id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="biz-pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {modalOpen && (
        <div className="biz-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div
            className="biz-modal-card biz-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{editId ? "Edit SMS Configuration" : "New SMS Configuration"}</h2>
            {error && modalOpen && (
              <p className="biz-banner biz-banner-error">{error}</p>
            )}
            <form className="biz-form-grid" onSubmit={onSubmit}>
              <label>
                Provider Name *
                <input
                  name="providername"
                  value={form.providername}
                  onChange={onChange}
                  required
                />
              </label>
              <label>
                Base URL *
                <input
                  name="baseurl"
                  value={form.baseurl}
                  onChange={onChange}
                  required
                />
              </label>
              <label>
                HTTP Method *
                <select name="method" value={form.method} onChange={onChange}>
                  <option value="">Select</option>
                  {HTTP_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Authorization Type
                <select
                  name="authorizationtype"
                  value={form.authorizationtype}
                  onChange={onChange}
                >
                  {AUTH_TYPES.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>

              {form.authorizationtype === "basic" && (
                <>
                  <label>
                    Basic Auth Username
                    <input
                      name="basicauthuserinfo"
                      value={form.basicauthuserinfo}
                      onChange={onChange}
                    />
                  </label>
                  <label>
                    Basic Auth Password
                    <input
                      type="password"
                      name="basicauthpasswordinfo"
                      value={form.basicauthpasswordinfo}
                      onChange={onChange}
                    />
                  </label>
                </>
              )}

              {form.authorizationtype === "bearer" && (
                <label className="biz-form-full">
                  Bearer Token
                  <input
                    name="bearertokeninfo"
                    value={form.bearertokeninfo}
                    onChange={onChange}
                  />
                </label>
              )}

              <label>
                Payload Type
                <select
                  name="payloadtype"
                  value={form.payloadtype}
                  onChange={onChange}
                >
                  {PAYLOAD_TYPES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>

              {form.payloadtype === "parameters" && (
                <div className="biz-form-full biz-sms-params">
                  <span>Parameters</span>
                  {form.parameter.map((param, i) => (
                    <div key={i} className="biz-sms-param-row">
                      <input
                        placeholder="Key"
                        value={param.key}
                        onChange={(e) =>
                          onParamChange(i, "key", e.target.value)
                        }
                      />
                      <input
                        placeholder="Value"
                        value={param.value}
                        onChange={(e) =>
                          onParamChange(i, "value", e.target.value)
                        }
                      />
                      <select
                        value={param.position}
                        onChange={(e) =>
                          onParamChange(i, "position", e.target.value)
                        }
                      >
                        {PARAM_POSITIONS.map((pos) => (
                          <option key={pos} value={pos}>
                            {pos}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="biz-btn-icon"
                        onClick={() => removeParam(i)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="biz-btn-secondary" onClick={addParam}>
                    Add parameter
                  </button>
                </div>
              )}

              {form.payloadtype === "rawjson" && (
                <label className="biz-form-full">
                  Raw JSON
                  <textarea
                    name="rawjson"
                    rows={4}
                    value={form.rawjson}
                    onChange={onChange}
                  />
                </label>
              )}

              <label className="biz-form-full">
                Mapping (JSON)
                <textarea
                  name="mapping"
                  rows={3}
                  value={form.mapping}
                  onChange={onChange}
                />
              </label>
              <label className="biz-form-full">
                Success Response (JSON) *
                <textarea
                  name="successresponse"
                  rows={3}
                  value={form.successresponse}
                  onChange={onChange}
                />
              </label>
              <label className="biz-form-full">
                Error Response (JSON) *
                <textarea
                  name="errorresponse"
                  rows={3}
                  value={form.errorresponse}
                  onChange={onChange}
                />
              </label>

              <div className="biz-modal-actions biz-form-full">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="biz-btn-primary" disabled={saving}>
                  {saving ? "Saving…" : editId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsConfig;
